<script setup>
/**
 * ROI Stats Block — editorial table treatment.
 *
 * Strict signal-to-noise discipline. The number IS the message; nothing
 * decorative is allowed to compete with it. Three deliberate moves:
 *
 *  - Two hairlines (top + bottom) frame the entire 3-stat row, like a
 *    financial-paper data table. No per-stat cards, no halos, no shadows.
 *  - Big number + small mono superscript unit, all left-aligned per
 *    column. The number is the alpha typographic element on its column.
 *  - One graphic flourish, only one: a short cyan accent rule (~3.5rem,
 *    soft glow) directly below each number. Replaces the sparkline as
 *    the "this is the headline figure" cue.
 *  - Microcontext line in mono italic cyan/70 below the label. This is
 *    the honest replacement for fake sparklines: instead of pretending
 *    to show a time series, we tell the reader in one short phrase WHAT
 *    is being measured ("medido en minutos productivos por jornada"...).
 *
 * Removed vs prior iteration: telemetry strip, delta chip, sparkline,
 * card chrome, top-left icon. All chartjunk that competed with the
 * number it was supposed to support.
 */
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

const stats = computed(() => [
  {
    value: t("landing.roi_stats.stats.0.value"),
    unit: t("landing.roi_stats.stats.0.unit"),
    label: t("landing.roi_stats.stats.0.label"),
    microcontext: t("landing.roi_stats.stats.0.microcontext"),
  },
  {
    value: t("landing.roi_stats.stats.1.value"),
    unit: t("landing.roi_stats.stats.1.unit"),
    label: t("landing.roi_stats.stats.1.label"),
    microcontext: t("landing.roi_stats.stats.1.microcontext"),
  },
  {
    value: t("landing.roi_stats.stats.2.value"),
    unit: t("landing.roi_stats.stats.2.unit"),
    label: t("landing.roi_stats.stats.2.label"),
    microcontext: t("landing.roi_stats.stats.2.microcontext"),
  },
]);
</script>

<template>
  <section class="relative py-24 lg:py-32 bg-inverse text-inverse-foreground overflow-hidden">
    <!-- Grid texture kept but at minimum opacity — barely-there ambient -->
    <div
      class="absolute inset-0 opacity-[0.03]"
      :style="{
        backgroundImage: 'linear-gradient(hsl(var(--inverse-accent)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--inverse-accent)) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }"
    />
    <!-- Single, very low ambient halo. No per-stat haloes anymore. -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[280px] bg-inverse-accent/[0.05] rounded-full blur-3xl" />

    <div class="container relative z-10 mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-16 lg:mb-20 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-inverse-accent mb-3">
          {{ t("landing.roi_stats.eyebrow") }}
        </p>
        <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight">
          {{ t("landing.roi_stats.title") }}
        </h2>
      </AnimatedSection>

      <!-- Editorial table: hairline frame, 3-column grid, no per-stat cards -->
      <div class="max-w-5xl mx-auto">
        <div class="border-t border-white/10" />

        <div class="grid grid-cols-1 md:grid-cols-3 gap-x-10 lg:gap-x-16 gap-y-14 py-14 lg:py-16">
          <AnimatedSection
            v-for="(stat, idx) in stats"
            :key="idx"
            animation="fade-up"
            :delay="idx * 100"
          >
            <!-- Big number with mono superscript unit. Centered on
                 mobile (single-column stack); left-aligned from md+
                 where the 3 columns sit side by side. -->
            <div class="flex items-start gap-2 mb-5 justify-center md:justify-start">
              <span
                class="text-7xl lg:text-8xl font-bold tabular-nums tracking-tight leading-none"
                :style="{ color: 'hsl(var(--inverse-foreground))' }"
              >
                {{ stat.value }}
              </span>
              <span class="font-mono text-base lg:text-lg font-semibold text-inverse-accent leading-none pt-2 whitespace-nowrap">
                {{ stat.unit }}
              </span>
            </div>

            <!-- Cyan accent rule. Centered on mobile via mx-auto. -->
            <div
              class="h-[2px] w-14 rounded-full mb-6 mx-auto md:mx-0"
              :style="{
                background: 'hsl(var(--inverse-accent))',
                boxShadow: '0 0 16px hsl(var(--inverse-accent) / 0.45)',
              }"
            />

            <p class="text-base text-inverse-foreground/95 leading-relaxed mb-3 max-w-[280px] mx-auto md:mx-0 text-center md:text-left">
              {{ stat.label }}
            </p>

            <p class="font-mono italic text-xs text-inverse-accent/70 leading-relaxed max-w-[260px] mx-auto md:mx-0 text-center md:text-left">
              {{ stat.microcontext }}
            </p>
          </AnimatedSection>
        </div>

        <div class="border-t border-white/10" />
      </div>

      <p class="text-xs text-inverse-muted-foreground text-center mt-8 max-w-2xl mx-auto">
        {{ t("landing.roi_stats.footnote") }}
      </p>
    </div>
  </section>
</template>
